import type L from "leaflet";

/**
 * True when a Polyline/Polygon actually holds a vertex, at any nesting depth.
 *
 * Two library behaviours meet here, and together they took down a whole page:
 *
 * 1. Deleting a drawn line by removing its vertices one at a time ends in Geoman's
 *    `Edit.Line._removeMarker` calling `this._layer.remove()`. That is Leaflet's own
 *    `remove()` — it detaches the layer from the MAP, but a FeatureGroup holding it keeps
 *    it, now with zero latlngs. The line looks deleted; `toGeoJSON()` still emits it as
 *    `{"coordinates": []}`. Geoman's `removeLayerBelowMinVertexCount` defaults to TRUE, so
 *    nothing stops the count reaching zero.
 * 2. Leaflet 1.9 opens a `permanent` tooltip the moment it is bound and asks the layer for
 *    its centre to place it. `polylineCenter`/`polygonCenter` THROW `latlngs not passed` on
 *    an empty ring — and a throw inside a `useEffect` reaches the router's error element,
 *    which replaces the entire application with its error page.
 *
 * So: check this before labelling a layer, and before serialising it for the server.
 *
 * Typed structurally on purpose. `L.Polygon` extends `L.Polyline` at runtime (and reaches
 * callers through an `instanceof L.Polyline` branch), but its GeoJSON type parameter makes
 * it unassignable to `L.Polyline`. All this needs is the vertex list.
 */
export function hasVertices(layer: { getLatLngs(): unknown }): boolean {
  const walk = (v: unknown): boolean =>
    Array.isArray(v) ? v.some(walk) : v != null;
  return walk(layer.getLatLngs());
}

/**
 * True when a GeoJSON geometry carries no coordinate at any depth.
 *
 * Takes `unknown` because the callers hold everything from `GeoJSON.Geometry` (a union that
 * includes GeometryCollection, which has no `coordinates` at all) to loosely-typed payloads
 * straight off `toGeoJSON()`. A GeometryCollection is judged by its members, so one holding
 * a real geometry is correctly reported as non-empty rather than dismissed for lacking a
 * `coordinates` key.
 */
export function isEmptyGeometry(geometry: unknown): boolean {
  const g = geometry as { coordinates?: unknown; geometries?: unknown } | null | undefined;
  if (!g) return true;
  if (Array.isArray(g.geometries)) return g.geometries.every(isEmptyGeometry);
  const walk = (v: unknown): boolean =>
    Array.isArray(v) ? v.some(walk) : typeof v === "number";
  return !walk(g.coordinates);
}

/**
 * Drop a layer from the group that owns it when Geoman removes it.
 *
 * Geoman detaches the layer from the map and fires `pm:remove`, but never touches the
 * FeatureGroup — so without this the "deleted" feature stays in the drawing: emptied of its
 * vertices when the user removed them one by one, fully intact when it was a marker (which
 * then quietly reappears on the next save).
 */
export function detachRemovedLayer(
  group: L.FeatureGroup | null | undefined,
  layer: L.Layer | undefined,
): void {
  if (group && layer) group.removeLayer(layer);
}
